import { ATTNInput } from '@components/ATTNInput';
import { LoaderBar } from '@components/LoaderBar';
import { StyledButton } from '@elements/Button';
import {
  ControlledTextAreaFormField,
  DropdownFormField,
} from '@elements/FormField';
import type { ISupplierOption } from '@hooks/purchase-order-items';
import { ATTNHelpText } from '@utility/constants/strings';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import type { IEmailData } from 'types/common/email';

export interface ICreateSupplierFormProps {
  control: Control;
  errors: Record<string, any>;
  emailLoading: boolean;
  emails: IEmailData[];
  supplierList: ISupplierOption[];
  loading: boolean;
  moveToNextStep?: () => void;
  supplierTabFinished: () => void;
  watchFields: {
    supplier_id?: string | number;
    email_addresses?: string[];
  };
  supplierChanged: (value: ISupplierOption) => void;
  isRetroActive: boolean;
  spList: ISupplierOption[];
  spLoading: boolean;
}

export function CreateSupplierForm({
  control,
  errors,
  emailLoading,
  emails,
  supplierList,
  loading,
  moveToNextStep,
  supplierTabFinished,
  watchFields,
  supplierChanged,
  isRetroActive,
  spList,
  spLoading,
}: ICreateSupplierFormProps) {
  const completed = useMemo(() => {
    const { supplier_id, email_addresses } = watchFields;
    if (!supplier_id) return false;
    if (!(email_addresses || []).length) return false;
    return true;
  }, [watchFields]);

  return (
    <div className="sk:px-3.5! sk:pb-3.5!">
      {loading && <LoaderBar />}

      <div className="sk:md:w-[87.5%]">
        <DropdownFormField
          options={supplierList}
          name="supplier_id"
          error={errors.supplier_id}
          placeholder="Select Supplier"
          control={control}
          defaultValue=""
          label="Select Supplier"
          dropdownChanged={supplierChanged}
        />
      </div>

      <ATTNInput
        emailLoading={emailLoading}
        emails={emails}
        control={control}
        helpText={ATTNHelpText.purchaseOrder}
        error={errors.email_addresses}
        disableNotify={isRetroActive}
        disabled={isRetroActive}
      />

      <ControlledTextAreaFormField
        label="Additional Note"
        placeholder="Note"
        rows={2}
        control={control}
        name="memo"
      />

      <div className="sk:flex sk:items-center sk:gap-2">
        <div className="sk:min-w-0 sk:flex-1">
          <DropdownFormField
            options={spList}
            name="destinations"
            error={errors.destinations}
            placeholder="Select Destinations"
            control={control}
            defaultValue=""
            label="Desired Destinations (Optional)"
            loading={spLoading}
            multiple
          />
        </div>

        <div className="sk:flex sk:shrink-0 sk:justify-end">
          <StyledButton
            type="button"
            className="no-break"
            onClick={() => {
              moveToNextStep?.();
              supplierTabFinished();
            }}
            disabled={!completed}
          >
            Enter Details
          </StyledButton>
        </div>
      </div>
    </div>
  );
}
